// 상시 경량 성능 텔레메트리 — 표본수집·비차단. "어디서 로딩/지연이 나나"를 잡기 위한 것.
// 깊은 현장 디버그는 lib/diag.ts(?diag=1, 100% 파일기록)와 별개.
// 수집정보는 클라에서 버킷화(osFamily/browserFamily/netEct)해 전송 — 원문 UA/IP 미전송.
"use client";

type PerfMeta = Record<string, unknown>;
type Row = {
  event: string;
  durationMs?: number;
  ok?: boolean;
  errorCode?: string;
  meta?: PerfMeta;
};

const ENDPOINT = "/api/telemetry";
// 표본 비율: 기본 100%(테스트기간 — 사용자 소수라 전부 수집). 운영 확장 시 env 로 낮추기(예: 0.1).
const SAMPLE = (() => {
  const v = Number(process.env.NEXT_PUBLIC_PERF_SAMPLE);
  return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 1;
})();
const FLUSH_MS = 5_000;
const MAX_QUEUE = 40; // 이 수 도달 시 즉시 flush
const BEACON_MAX = 100; // 1회 전송 행수 상한(sendBeacon 64KB 대비)

let active: boolean | null = null;
let sid = "";
let env: Record<string, unknown> = {};
const q: Row[] = [];

function snapshotEnv(): Record<string, unknown> {
  const ua = navigator.userAgent || "";
  const c = (navigator as unknown as { connection?: { effectiveType?: string; downlink?: number } }).connection;
  let standalone = 0;
  try {
    standalone = window.matchMedia("(display-mode: standalone)").matches ? 1 : 0;
  } catch {
    /* noop */
  }
  return {
    osFamily: /iPhone|iPad|iPod|iOS/.test(ua)
      ? "iOS"
      : /Android/.test(ua)
        ? "Android"
        : /Windows/.test(ua)
          ? "Windows"
          : /Macintosh|Mac OS/.test(ua)
            ? "macOS"
            : /Linux/.test(ua)
              ? "Linux"
              : "other",
    browserFamily: /Edg\//.test(ua)
      ? "Edge"
      : /CriOS|Chrome|Chromium/.test(ua)
        ? "Chrome"
        : /FxiOS|Firefox/.test(ua)
          ? "Firefox"
          : /Safari/.test(ua)
            ? "Safari"
            : "other",
    netEct: c?.effectiveType,
    netDownlink: typeof c?.downlink === "number" ? c.downlink : undefined,
    appVersion: process.env.NEXT_PUBLIC_APP_VERSION,
    // 보조 정보(meta 로 첫 page_load 에 함께 실림)
    dpr: typeof window.devicePixelRatio === "number" ? window.devicePixelRatio : undefined,
    screen: typeof screen !== "undefined" ? `${screen.width}x${screen.height}` : undefined,
    cores: (navigator as unknown as { hardwareConcurrency?: number }).hardwareConcurrency,
    mem: (navigator as unknown as { deviceMemory?: number }).deviceMemory,
    standalone,
  };
}

/** 표본 여부를 결정하고 flush 타이머/리스너를 설치. 멱등. 비활성 세션이면 false. */
export function initPerf(): boolean {
  if (active !== null) return active;
  if (typeof window === "undefined") return (active = false);
  try {
    const forced = new URLSearchParams(window.location.search).has("diag");
    active = forced || Math.random() < SAMPLE;
  } catch {
    active = false;
  }
  if (!active) return false;

  sid = (globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)).slice(0, 12);
  env = snapshotEnv();

  setInterval(flush, FLUSH_MS);
  // iOS/모바일은 unload 신뢰불가 → visibilitychange(hidden)·pagehide 만 신뢰(diag.ts 와 동일 사상)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);
  void sendClientProfile(); // 세션당 1회: 브라우저 수집가능 정보 전체 기록
  setTimeout(sendPaintProbe, 5000); // 화면이 다 그려진 뒤 — 밝은 진입 화면이 어둡게 나오는 기기만 걸러 보낸다
  return true;
}

// 밝은 진입 화면(.entry-light: 홈·로그인·등록)이 **어둡게 나온다**는 신고를 기기 안에서 재 오는 탐침.
//   2026-10-12 사무실 전자칠판(안드로이드 8 MS848·WebView 크롬 138): 밝은 배경이 통째로 빠져 검은 바탕에
//   검정 제목이 묻혔다. 크롬 79·108·최신에 같은 화면 크기·배율·다크 설정까지 맞춰도 재현이 안 돼 원인을 못 가렸다.
//   그래서 그 기기가 실제로 계산한 값(배경·규칙·매체 질의·끼워 넣은 스타일)을 남긴다.
// 아무 기기나 보내지 않는다 — 배경이 기대값과 다르거나, 브라우저가 화면을 건드릴 낌새(다크·강제 색·앱 속 WebView)가 있을 때만.
function sendPaintProbe(): void {
  try {
    const el = document.querySelector(".entry-light");
    if (!el) return;
    const mm = (q: string) => { try { return window.matchMedia(q).matches; } catch { return null; } };
    const cs = (e: Element | null) => {
      if (!e) return null;
      const c = getComputedStyle(e);
      return { bg: c.backgroundColor, img: c.backgroundImage.slice(0, 60), color: c.color, scheme: c.colorScheme, filter: c.filter.slice(0, 40) };
    };
    const root = cs(el);
    const media = {
      dark: mm("(prefers-color-scheme: dark)"), forced: mm("(forced-colors: active)"),
      inverted: mm("(inverted-colors: inverted)"), contrast: mm("(prefers-contrast: more)"),
    };
    const wv = navigator.userAgent.indexOf("; wv)") !== -1;
    const offBg = root?.bg !== "rgb(238, 243, 248)";
    if (!offBg && !wv && !media.dark && !media.forced && !media.inverted) return;
    // .entry-light 규칙이 실제로 들어와 있는지(파일이 왔는지·덮였는지) + 시트 목록
    const rules: unknown[] = [];
    const sheets: string[] = [];
    for (let i = 0; i < document.styleSheets.length; i++) {
      const sh = document.styleSheets[i];
      let n: number | string = -1;
      try {
        n = sh.cssRules.length;
        const walk = (list: CSSRuleList, depth: number) => {
          for (let k = 0; k < list.length; k++) {
            const r = list[k] as CSSStyleRule & { cssRules?: CSSRuleList };
            if (r.selectorText && /\.entry-light\s*$/.test(r.selectorText) && rules.length < 6) rules.push({ sel: r.selectorText.slice(0, 40), sheet: i, depth, bg: r.style.background.slice(0, 70), bgc: r.style.backgroundColor });
            if (r.cssRules && depth < 4) walk(r.cssRules, depth + 1);
          }
        };
        walk(sh.cssRules, 0);
      } catch { n = "blocked"; }
      sheets.push(`${(sh.href || "inline").slice(-36)}:${n}${sh.disabled ? ":off" : ""}`);
    }
    // 우리가 넣지 않은 <style>(브라우저 앱의 야간 모드 등이 끼워 넣는 것)
    const styles = Array.from(document.querySelectorAll("style")).map((st) => ({
      at: Array.from(st.attributes).map((a) => a.name).join(","),
      head: (st.textContent || "").slice(0, 90),
    })).slice(0, 12);
    perf("paint_probe", undefined, {
      ua: navigator.userAgent, vp: `${window.innerWidth}x${window.innerHeight}`, dpr: window.devicePixelRatio,
      media, root, inline: (el as HTMLElement).style.backgroundColor, html: cs(document.documentElement), body: cs(document.body),
      h: cs(document.querySelector(".pair-h")), digit: cs(document.querySelector(".pair-digit")),
      htmlAttrs: Array.from(document.documentElement.attributes).map((a) => `${a.name}=${a.value}`).join(" ").slice(0, 200),
      rules, sheets, styles,
      legacy: !!document.querySelector('link[href*="legacy-tw"]'),
    }, !offBg);
    flush();
  } catch {
    /* 탐침 실패는 조용히 무시 */
  }
}

// 브라우저에서 수집 가능한 정보를 최대한 모아 1회 전송(접근시간·버전·OS·화면·네트워크 등 모두).
async function sendClientProfile(): Promise<void> {
  try {
    const nav = navigator as unknown as {
      hardwareConcurrency?: number; deviceMemory?: number; maxTouchPoints?: number;
      languages?: readonly string[]; userAgentData?: { getHighEntropyValues?: (h: string[]) => Promise<unknown> };
      connection?: { effectiveType?: string; downlink?: number; rtt?: number; saveData?: boolean };
    };
    const profile: Record<string, unknown> = {
      ua: navigator.userAgent, // 브라우저 버전 + OS 포함(전체)
      lang: navigator.language,
      langs: nav.languages,
      tz: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return undefined; } })(),
      screen: typeof screen !== "undefined" ? `${screen.width}x${screen.height}` : undefined,
      avail: typeof screen !== "undefined" ? `${screen.availWidth}x${screen.availHeight}` : undefined,
      colorDepth: typeof screen !== "undefined" ? screen.colorDepth : undefined,
      dpr: window.devicePixelRatio,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      cores: nav.hardwareConcurrency,
      mem: nav.deviceMemory,
      touch: nav.maxTouchPoints,
      standalone: (() => { try { return window.matchMedia("(display-mode: standalone)").matches ? 1 : 0; } catch { return 0; } })(),
      net: nav.connection
        ? { ect: nav.connection.effectiveType, downlink: nav.connection.downlink, rtt: nav.connection.rtt, saveData: nav.connection.saveData }
        : undefined,
    };
    // UA Client Hints 고엔트로피(크로미움) — 정확한 브라우저/OS 버전·기기 모델. Safari/FF 는 미지원→UA 문자열로 대체.
    if (nav.userAgentData?.getHighEntropyValues) {
      try {
        profile.uaCH = await nav.userAgentData.getHighEntropyValues([
          "platform", "platformVersion", "architecture", "model", "uaFullVersion", "fullVersionList", "bitness", "mobile",
        ]);
      } catch {
        /* 미지원/거부 */
      }
    }
    perf("client_profile", undefined, profile);
  } catch {
    /* 수집 실패는 조용히 무시 */
  }
}

export function isPerfActive(): boolean {
  return active === true;
}

/** 성능/지연 이벤트 1건 기록. 비활성 세션이면 no-op. */
export function perf(event: string, durationMs?: number, meta?: PerfMeta, ok = true, errorCode?: string): void {
  if (active !== true) return;
  q.push({
    event,
    durationMs: durationMs != null ? Math.round(durationMs) : undefined,
    ok,
    errorCode,
    meta,
  });
  if (q.length >= MAX_QUEUE) flush();
}

/** 쌓인 기록을 지금 보낸다 — 화면이 꺼지기 직전처럼 다음 주기를 기다릴 수 없을 때. */
export function flushPerf(): void {
  flush();
}

function flush(): void {
  if (active !== true || q.length === 0) return;
  const events = q.splice(0, q.length).slice(0, BEACON_MAX);
  const body = JSON.stringify({
    sid,
    route: location.pathname,
    env,
    events,
  });
  try {
    const ok = navigator.sendBeacon?.(ENDPOINT, new Blob([body], { type: "application/json" }));
    if (!ok) {
      void fetch(ENDPOINT, {
        method: "POST",
        body,
        keepalive: true,
        headers: { "Content-Type": "application/json" },
      }).catch(() => {});
    }
  } catch {
    /* 전송 실패는 조용히 버림 — 텔레메트리가 앱 동작에 영향 주지 않게 */
  }
}
